import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [approvalSummary, setApprovalSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewModal, setReviewModal] = useState({ show: false, request: null, action: null });
  const [reviewComment, setReviewComment] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Approval history state
  const [approvalHistory, setApprovalHistory] = useState({ records: [], summary: {} });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState({
    period: 'month', // 'month' or 'year'
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchApprovalHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyFilter]);

  const fetchData = async () => {
    try {
      const [statsRes, approvalRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/approval-requests/summary`)
      ]);
      setStats(statsRes.data);
      setApprovalSummary(approvalRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovalHistory = async () => {
    setHistoryLoading(true);
    try {
      const params = { year: historyFilter.year };
      if (historyFilter.period === 'month') {
        params.month = historyFilter.month;
      }
      const res = await axios.get(`${API}/approval-requests/history`, { params });
      setApprovalHistory(res.data);
    } catch (error) {
      console.error('Error fetching approval history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB') + ' ' + date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const getModuleLabel = (module) => {
    const labels = { po: 'Purchase Order', vendor: 'Vendor', vendor_item_price: 'Item Price', challan: 'Challan', customer: 'Customer' };
    return labels[module] || module;
  };

  const getActionLabel = (action) => {
    const labels = { create: 'Create', update: 'Update', delete: 'Delete' };
    return labels[action] || action;
  };

  const getActionColor = (action) => {
    const colors = {
      create: 'bg-green-100 text-green-800',
      update: 'bg-blue-100 text-blue-800',
      delete: 'bg-red-100 text-red-800'
    };
    return colors[action] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const openReviewModal = (request, action) => {
    setReviewModal({ show: true, request, action });
    setReviewComment('');
  };

  const closeReviewModal = () => {
    setReviewModal({ show: false, request: null, action: null });
    setReviewComment('');
  };

  const handleReview = async () => {
    if (!reviewModal.request) return;
    
    setProcessing(true);
    try {
      const endpoint = reviewModal.action === 'approve' 
        ? `${API}/approval-requests/${reviewModal.request.id}/approve`
        : `${API}/approval-requests/${reviewModal.request.id}/reject`;
      
      await axios.put(endpoint, { comments: reviewComment });
      
      // Refresh data
      await fetchData();
      closeReviewModal();
    } catch (error) {
      console.error('Error reviewing request:', error);
      alert(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setProcessing(false);
    }
  };

  const getEntitySummary = (request) => {
    const data = request.entity_data || request.original_data || {};
    if (request.module === 'po') {
      return data.po_number || data.vendor_name || 'New PO';
    } else if (request.module === 'vendor') {
      return data.name || 'New Vendor';
    } else if (request.module === 'vendor_item_price') {
      return `${data.partcode} @ ${data.vendor_name}`;
    } else if (request.module === 'challan') {
      return data.challan_number || 'Challan';
    } else if (request.module === 'customer') {
      return data.name || 'Customer';
    }
    return '-';
  };

  const getPriceChangeInfo = (request) => {
    if (request.module === 'vendor_item_price') {
      const data = request.entity_data || {};
      return {
        currentPrice: data.current_price,
        newPrice: data.new_price,
        difference: data.difference,
        differencePercent: data.difference_percent
      };
    } else if (request.module === 'po' && request.entity_data?.price_changes?.length > 0) {
      return {
        priceChanges: request.entity_data.price_changes
      };
    }
    return null;
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  const isAdmin = approvalSummary?.is_admin;

  return (
    <div className="space-y-6" data-testid="dashboard">
      <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>

      {/* Approval Requests Section */}
      {approvalSummary && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg shadow p-5 border border-amber-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {isAdmin ? '🔐 Approval Queue' : '📋 My Requests'}
            </h2>
            <div className="flex gap-3">
              <div className="flex items-center gap-2 px-3 py-1 bg-yellow-100 rounded-full">
                <span className="text-yellow-600 font-bold">{approvalSummary.pending_count}</span>
                <span className="text-yellow-700 text-sm">Pending</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1 bg-green-100 rounded-full">
                <span className="text-green-600 font-bold">{approvalSummary.approved_count}</span>
                <span className="text-green-700 text-sm">Approved</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1 bg-red-100 rounded-full">
                <span className="text-red-600 font-bold">{approvalSummary.rejected_count}</span>
                <span className="text-red-700 text-sm">Rejected</span>
              </div>
            </div>
          </div>

          {/* Pending Requests */}
          {approvalSummary.recent_pending && approvalSummary.recent_pending.length > 0 ? (
            <div className="bg-white rounded-lg overflow-hidden mb-4">
              <div className="px-4 py-2 bg-yellow-100 border-b border-yellow-200">
                <h3 className="font-medium text-yellow-800">
                  {isAdmin ? 'Pending Approvals' : 'Your Pending Requests'}
                </h3>
              </div>
              <div className="divide-y divide-gray-100">
                {approvalSummary.recent_pending.map((request) => {
                  const priceInfo = getPriceChangeInfo(request);
                  return (
                    <div key={request.id} className="px-4 py-3 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getActionColor(request.action_type)}`}>
                            {getActionLabel(request.action_type)}
                          </span>
                          <div>
                            <div className="font-medium text-gray-900">
                              {getModuleLabel(request.module)}: {getEntitySummary(request)}
                            </div>
                            <div className="text-xs text-gray-500">
                              By {request.requested_by_name} • {formatDate(request.requested_at)}
                            </div>
                          </div>
                        </div>
                        {isAdmin && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => openReviewModal(request, 'approve')}
                              className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => openReviewModal(request, 'reject')}
                              className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Price Change Info */}
                      {priceInfo && (
                        <div className="mt-2 ml-12 p-2 bg-amber-50 rounded border border-amber-200">
                          {priceInfo.currentPrice !== undefined && (
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-gray-600">Price Change:</span>
                              <span className="font-medium text-gray-700">Rs.{priceInfo.currentPrice}</span>
                              <span className="text-gray-400">→</span>
                              <span className={`font-bold ${priceInfo.difference > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                Rs.{priceInfo.newPrice}
                              </span>
                              {priceInfo.differencePercent !== null && (
                                <span className={`text-xs px-2 py-0.5 rounded ${priceInfo.difference > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                  {priceInfo.difference > 0 ? '+' : ''}{priceInfo.differencePercent}%
                                </span>
                              )}
                            </div>
                          )}
                          {priceInfo.priceChanges && (
                            <div className="text-sm">
                              <span className="text-amber-700 font-medium">⚠️ {priceInfo.priceChanges.length} item(s) with price changes:</span>
                              <div className="mt-1 space-y-1">
                                {priceInfo.priceChanges.map((pc, idx) => (
                                  <div key={idx} className="flex items-center gap-2 text-xs">
                                    <span className="font-medium">{pc.partcode}:</span>
                                    <span className="text-gray-600">Rs.{pc.current_price}</span>
                                    <span className="text-gray-400">→</span>
                                    <span className={`font-bold ${pc.difference > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                      Rs.{pc.new_price}
                                    </span>
                                    <span className={`px-1 rounded ${pc.difference > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                      {pc.difference > 0 ? '+' : ''}{pc.difference_percent}%
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg p-4 text-center text-gray-500 mb-4">
              {isAdmin ? 'No pending approvals' : 'You have no pending requests'}
            </div>
          )}

          {/* Recently Reviewed */}
          {approvalSummary.recent_reviewed && approvalSummary.recent_reviewed.length > 0 && (
            <div className="bg-white rounded-lg overflow-hidden">
              <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
                <h3 className="font-medium text-gray-700">
                  {isAdmin ? 'Recently Reviewed by You' : 'Recent Decisions'}
                </h3>
              </div>
              <div className="divide-y divide-gray-100">
                {approvalSummary.recent_reviewed.map((request) => (
                  <div key={request.id} className="px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(request.status)}`}>
                        {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                      </span>
                      <div>
                        <div className="font-medium text-gray-900">
                          {getModuleLabel(request.module)}: {getEntitySummary(request)}
                        </div>
                        <div className="text-xs text-gray-500">
                          Requested by {request.requested_by_name} • {formatDate(request.requested_at)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {isAdmin && request.reviewed_by_name && `Reviewed by ${request.reviewed_by_name} • `}
                          {formatDate(request.reviewed_at)}
                          {request.review_comments && ` • "${request.review_comments}"`}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6" data-testid="stat-schedules">
          <div className="text-sm text-gray-600 mb-1">Total Schedules</div>
          <div className="text-3xl font-bold text-blue-600">{stats?.total_schedules || 0}</div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6" data-testid="stat-dispatches-today">
          <div className="text-sm text-gray-600 mb-1">Dispatches Today</div>
          <div className="text-3xl font-bold text-green-600">{stats?.dispatches_today || 0}</div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6" data-testid="stat-production-today">
          <div className="text-sm text-gray-600 mb-1">Production Records Today</div>
          <div className="text-3xl font-bold text-purple-600">{stats?.production_today || 0}</div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6" data-testid="stat-customers">
          <div className="text-sm text-gray-600 mb-1">Total Customers</div>
          <div className="text-3xl font-bold text-orange-600">{stats?.total_customers || 0}</div>
        </div>
      </div>

      {/* Recent Activities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Dispatches */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Recent Dispatches</h2>
          </div>
          <div className="p-6">
            {stats?.recent_dispatches && stats.recent_dispatches.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_dispatches.map((dispatch, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <div className="font-medium text-gray-900">{dispatch.partcode}</div>
                      <div className="text-sm text-gray-600">{dispatch.customer}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-green-600">{dispatch.quantity} units</div>
                      <div className="text-xs text-gray-500">{dispatch.dispatch_date}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No recent dispatches</p>
            )}
          </div>
        </div>

        {/* Recent Production */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Recent Production</h2>
          </div>
          <div className="p-6">
            {stats?.recent_production && stats.recent_production.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_production.map((production, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <div className="font-medium text-gray-900">{production.station}</div>
                      <div className="text-sm text-gray-600">{production.partcode}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-purple-600">
                        {production.actual_quantity}/{production.target_quantity}
                      </div>
                      <div className="text-xs text-gray-500">{production.production_date}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No recent production</p>
            )}
          </div>
        </div>
      </div>

      {/* Approval History Section */}
      <div className="bg-white rounded-lg shadow mt-6">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h2 className="text-xl font-semibold text-gray-900">
              📋 Approval History {isAdmin ? '(All Users)' : '(Your Requests)'}
            </h2>
            
            {/* Filters */}
            <div className="flex items-center gap-3">
              {/* Period Toggle */}
              <div className="flex rounded-lg overflow-hidden border border-gray-300">
                <button
                  onClick={() => setHistoryFilter(f => ({ ...f, period: 'month' }))}
                  className={`px-3 py-1.5 text-sm ${historyFilter.period === 'month' ? 'bg-red-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setHistoryFilter(f => ({ ...f, period: 'year' }))}
                  className={`px-3 py-1.5 text-sm ${historyFilter.period === 'year' ? 'bg-red-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                >
                  Yearly
                </button>
              </div>
              
              {/* Month Selector (only if monthly) */}
              {historyFilter.period === 'month' && (
                <select
                  value={historyFilter.month}
                  onChange={(e) => setHistoryFilter(f => ({ ...f, month: parseInt(e.target.value) }))}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                >
                  {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, idx) => (
                    <option key={idx} value={idx + 1}>{m}</option>
                  ))}
                </select>
              )}
              
              {/* Year Selector */}
              <select
                value={historyFilter.year}
                onChange={(e) => setHistoryFilter(f => ({ ...f, year: parseInt(e.target.value) }))}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              >
                {[2023, 2024, 2025, 2026].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Summary Stats */}
          <div className="flex gap-6 mt-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{approvalHistory.summary?.total || 0}</div>
              <div className="text-xs text-gray-500">Total</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{approvalHistory.summary?.pending || 0}</div>
              <div className="text-xs text-gray-500">Pending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{approvalHistory.summary?.approved || 0}</div>
              <div className="text-xs text-gray-500">Approved</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{approvalHistory.summary?.rejected || 0}</div>
              <div className="text-xs text-gray-500">Rejected</div>
            </div>
          </div>
        </div>
        
        {/* History List */}
        <div className="max-h-96 overflow-y-auto">
          {historyLoading ? (
            <div className="p-6 text-center text-gray-500">Loading...</div>
          ) : approvalHistory.records?.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested By</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  {isAdmin && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reviewed By</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {approvalHistory.records.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{formatDate(record.requested_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getActionColor(record.action_type)}`}>
                        {getActionLabel(record.action_type)}
                      </span>
                      <span className="ml-1 text-xs text-gray-600">{getModuleLabel(record.module)}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{getEntitySummary(record)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{record.requested_by_name}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(record.status)}`}>
                        {record.status?.charAt(0).toUpperCase() + record.status?.slice(1)}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {record.reviewed_by_name || '-'}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-6 text-center text-gray-500">
              No approval records found for {historyFilter.period === 'month' ? 
                `${['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][historyFilter.month - 1]} ${historyFilter.year}` : 
                historyFilter.year}
            </div>
          )}
        </div>
      </div>

      {/* Review Modal with Full Details Preview */}
      {reviewModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {reviewModal.action === 'approve' ? '✅ Approve Request' : '❌ Reject Request'}
            </h2>
            
            {/* Request Summary */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-600">Request Type</div>
                  <div className="font-medium">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getActionColor(reviewModal.request?.action_type)}`}>
                      {getActionLabel(reviewModal.request?.action_type)}
                    </span>
                    <span className="ml-2">{getModuleLabel(reviewModal.request?.module)}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500">Requested by</div>
                  <div className="text-sm font-medium">{reviewModal.request?.requested_by_name}</div>
                  <div className="text-xs text-gray-400">{formatDate(reviewModal.request?.requested_at)}</div>
                </div>
              </div>
            </div>

            {/* Full Read-Only Preview */}
            {reviewModal.request && (
              <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-blue-50 px-4 py-2 border-b border-gray-200">
                  <h3 className="font-medium text-blue-800">📋 Full Details Preview</h3>
                </div>
                <div className="p-4 bg-white">
                  {/* PO Preview */}
                  {reviewModal.request.module === 'po' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <div className="text-xs text-gray-500">PO Number</div>
                          <div className="font-semibold">{reviewModal.request.entity_data?.po_number || '-'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Vendor</div>
                          <div className="font-semibold">{reviewModal.request.entity_data?.vendor_name || '-'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">PO Date</div>
                          <div className="font-semibold">{reviewModal.request.entity_data?.po_date || '-'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Expected Completion</div>
                          <div className="font-semibold">{reviewModal.request.entity_data?.expected_completion_date || '-'}</div>
                        </div>
                      </div>
                      
                      {/* PO Items Table */}
                      {reviewModal.request.entity_data?.items?.length > 0 && (
                        <div>
                          <div className="text-sm font-medium text-gray-700 mb-2">Items ({reviewModal.request.entity_data.items.length})</div>
                          <table className="w-full text-sm border border-gray-200 rounded">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-3 py-2 text-left">Part Code</th>
                                <th className="px-3 py-2 text-left">Description</th>
                                <th className="px-3 py-2 text-left">Type</th>
                                <th className="px-3 py-2 text-right">Qty</th>
                                <th className="px-3 py-2 text-right">Rate</th>
                                <th className="px-3 py-2 text-right">Amount</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {reviewModal.request.entity_data.items.map((item, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                  <td className="px-3 py-2 font-medium">{item.partcode}</td>
                                  <td className="px-3 py-2">{item.description}</td>
                                  <td className="px-3 py-2">
                                    <span className={`px-2 py-0.5 rounded text-xs ${item.item_type === 'Job Work' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                                      {item.item_type}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-right">{item.quantity}</td>
                                  <td className="px-3 py-2 text-right">₹{item.rate_per_piece?.toLocaleString('en-IN')}</td>
                                  <td className="px-3 py-2 text-right font-medium">₹{(item.quantity * item.rate_per_piece)?.toLocaleString('en-IN')}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      
                      {/* PO Totals */}
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex justify-end">
                          <div className="w-64 space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Subtotal:</span>
                              <span className="font-medium">₹{reviewModal.request.entity_data?.subtotal?.toLocaleString('en-IN') || '0'}</span>
                            </div>
                            {reviewModal.request.entity_data?.cgst_amount > 0 && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">CGST ({reviewModal.request.entity_data?.cgst_percent}%):</span>
                                <span>₹{reviewModal.request.entity_data?.cgst_amount?.toLocaleString('en-IN')}</span>
                              </div>
                            )}
                            {reviewModal.request.entity_data?.sgst_amount > 0 && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">SGST ({reviewModal.request.entity_data?.sgst_percent}%):</span>
                                <span>₹{reviewModal.request.entity_data?.sgst_amount?.toLocaleString('en-IN')}</span>
                              </div>
                            )}
                            <div className="flex justify-between border-t pt-1 text-base font-bold">
                              <span>Total:</span>
                              <span className="text-green-700">₹{reviewModal.request.entity_data?.total_amount?.toLocaleString('en-IN') || '0'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {reviewModal.request.entity_data?.remarks && (
                        <div>
                          <div className="text-xs text-gray-500">Remarks</div>
                          <div className="text-sm bg-yellow-50 p-2 rounded">{reviewModal.request.entity_data.remarks}</div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Vendor Preview */}
                  {reviewModal.request.module === 'vendor' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">Vendor Name</div>
                        <div className="font-semibold text-lg">{reviewModal.request.entity_data?.name || '-'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Contact Person</div>
                        <div className="font-medium">{reviewModal.request.entity_data?.contact_person || '-'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Phone</div>
                        <div className="font-medium">{reviewModal.request.entity_data?.phone || '-'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Email</div>
                        <div className="font-medium">{reviewModal.request.entity_data?.email || '-'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">GST Number</div>
                        <div className="font-medium">{reviewModal.request.entity_data?.gst_number || '-'}</div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-xs text-gray-500">Address</div>
                        <div className="font-medium">{reviewModal.request.entity_data?.address || '-'}</div>
                      </div>
                    </div>
                  )}

                  {/* Challan Preview */}
                  {reviewModal.request.module === 'challan' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <div className="text-xs text-gray-500">Challan Number</div>
                          <div className="font-semibold">{reviewModal.request.entity_data?.challan_number || '-'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Vendor</div>
                          <div className="font-semibold">{reviewModal.request.entity_data?.vendor_name || '-'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Challan Date</div>
                          <div className="font-semibold">{reviewModal.request.entity_data?.challan_date || '-'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Status</div>
                          <div className="font-semibold">{reviewModal.request.entity_data?.status || '-'}</div>
                        </div>
                      </div>
                      {reviewModal.request.entity_data?.items?.length > 0 && (
                        <div>
                          <div className="text-sm font-medium text-gray-700 mb-2">Items</div>
                          <div className="space-y-2">
                            {reviewModal.request.entity_data.items.map((item, idx) => (
                              <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                                <div>
                                  <div className="font-medium">{item.partcode}</div>
                                  <div className="text-xs text-gray-500">{item.description}</div>
                                </div>
                                <div className="text-right">
                                  <div className="font-semibold">{item.quantity} {item.unit}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Customer Preview */}
                  {reviewModal.request.module === 'customer' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">Customer Name</div>
                        <div className="font-semibold text-lg">{reviewModal.request.entity_data?.name || '-'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Contact Person</div>
                        <div className="font-medium">{reviewModal.request.entity_data?.contact_person || '-'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Phone</div>
                        <div className="font-medium">{reviewModal.request.entity_data?.phone || '-'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Email</div>
                        <div className="font-medium">{reviewModal.request.entity_data?.email || '-'}</div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-xs text-gray-500">Address</div>
                        <div className="font-medium">{reviewModal.request.entity_data?.address || '-'}</div>
                      </div>
                    </div>
                  )}

                  {/* Vendor Item Price Preview */}
                  {reviewModal.request.module === 'vendor_item_price' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">Part Code</div>
                        <div className="font-semibold">{reviewModal.request.entity_data?.partcode || '-'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Vendor</div>
                        <div className="font-semibold">{reviewModal.request.entity_data?.vendor_name || '-'}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Price Change Details */}
            {reviewModal.request && (() => {
              const priceInfo = getPriceChangeInfo(reviewModal.request);
              if (!priceInfo) return null;
              return (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="text-sm font-medium text-amber-800 mb-2">⚠️ Price Change Details</div>
                  {priceInfo.currentPrice !== undefined && (
                    <div className="flex items-center gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">Current Price:</span>
                        <span className="ml-2 font-bold text-gray-800">Rs.{priceInfo.currentPrice}</span>
                      </div>
                      <span className="text-gray-400">→</span>
                      <div>
                        <span className="text-gray-600">New Price:</span>
                        <span className={`ml-2 font-bold ${priceInfo.difference > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          Rs.{priceInfo.newPrice}
                        </span>
                      </div>
                      {priceInfo.differencePercent !== null && (
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${priceInfo.difference > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                          {priceInfo.difference > 0 ? '+' : ''}{priceInfo.differencePercent}%
                        </span>
                      )}
                    </div>
                  )}
                  {priceInfo.priceChanges && (
                    <div className="space-y-2">
                      <div className="text-sm text-amber-700">{priceInfo.priceChanges.length} item(s) with price changes:</div>
                      <table className="w-full text-xs">
                        <thead className="bg-amber-100">
                          <tr>
                            <th className="px-2 py-1 text-left">Part Code</th>
                            <th className="px-2 py-1 text-right">Current</th>
                            <th className="px-2 py-1 text-right">New</th>
                            <th className="px-2 py-1 text-right">Change</th>
                          </tr>
                        </thead>
                        <tbody>
                          {priceInfo.priceChanges.map((pc, idx) => (
                            <tr key={idx} className="border-b border-amber-100">
                              <td className="px-2 py-1 font-medium">{pc.partcode}</td>
                              <td className="px-2 py-1 text-right">Rs.{pc.current_price}</td>
                              <td className={`px-2 py-1 text-right font-medium ${pc.difference > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                Rs.{pc.new_price}
                              </td>
                              <td className={`px-2 py-1 text-right ${pc.difference > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                {pc.difference > 0 ? '+' : ''}{pc.difference_percent}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {reviewModal.action === 'approve' && (
                        <div className="text-xs text-amber-700 mt-2 p-2 bg-amber-100 rounded">
                          ℹ️ Approving will update vendor item prices and create price history entries.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Comments (optional)
              </label>
              <textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                rows={3}
                placeholder={reviewModal.action === 'reject' ? 'Reason for rejection...' : 'Any additional notes...'}
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={closeReviewModal}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={processing}
              >
                Cancel
              </button>
              <button
                onClick={handleReview}
                disabled={processing}
                className={`px-4 py-2 text-white rounded-lg ${
                  reviewModal.action === 'approve' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {processing ? 'Processing...' : reviewModal.action === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
